import type { ShapeOption, FontSettings, SliderConfig, AnimationType } from '@/types'

// Persian short connector words - need tight spacing (canonical list lives in v2 layout)
export { PERSIAN_SHORT_CONNECTORS } from '@/v2/layout/persianConnectors'

// Animation type options
export interface AnimationOption {
  value: AnimationType
  label: string
}

export const ANIMATION_OPTIONS: AnimationOption[] = [
  { value: 'linear', label: 'Linear' },
  { value: 'spiral', label: 'Spiral' },
  { value: 'wave', label: 'Wave' },
  { value: 'bounce', label: 'Bounce' },
  { value: 'cascade', label: 'Cascade' },
  { value: 'vortex', label: 'Vortex' },
  { value: 'gravity', label: 'Gravity' },
  { value: 'scatter', label: 'Scatter' },
  { value: 'elastic', label: 'Elastic' },
  { value: 'typewriter', label: 'Typewriter' },
  { value: 'pen', label: 'Qalam sweep' },
  { value: 'dots', label: 'Dots last' },
  { value: 'words', label: 'Word by word' },
  { value: 'bleed', label: 'Ink bleed' },
  { value: 'sandstorm', label: 'Sandstorm' },
  { value: 'magnet', label: 'Magnetic' },
  { value: 'rain', label: 'Rain settle' },
  { value: 'breathing', label: 'Breathing' },
  { value: 'shimmer', label: 'Shimmer sweep' },
  { value: 'flow', label: 'Flowing ink' },
]

// Shape options for dropdown
export const SHAPE_OPTIONS: ShapeOption[] = [
  { value: 'x', label: 'X' },
  { value: 'square', label: 'Square' },
  { value: 'circle', label: 'Circle' },
  { value: 'diamond', label: 'Diamond' },
  { value: 'star', label: 'Star' },
  { value: 'dot', label: 'Dot' },
  { value: 'triangle', label: 'Triangle' },
  { value: 'hexagon', label: 'Hexagon' },
  { value: 'cross', label: 'Cross' },
  { value: 'heart', label: 'Heart' },
  { value: 'crescent', label: 'Crescent' },
  { value: 'ring', label: 'Ring' },
  { value: 'svg', label: 'Custom SVG' },
]

// Default text
export const DEFAULT_TEXT = 'بسم الله'

// Default font name
export const DEFAULT_FONT = 'Nastaliq'
export const DEFAULT_FONT_ASSET_PATH = '/fonts/wm_Mirza_Jali.ttf'

// Default module color (warm cream/gold)
export const DEFAULT_COLOR = { r: 18, g: 18, b: 18 }
export const DEFAULT_BACKGROUND_COLOR = { r: 255, g: 255, b: 255 }
export const DEFAULT_CHAOS_COLOR = { r: 26, g: 26, b: 46 }
export const DEFAULT_BORDER_COLOR = { r: 255, g: 255, b: 255 }
export const DEFAULT_GLOW_COLOR = { r: 232, g: 213, b: 183 }

// Default font settings
export const DEFAULT_FONT_SETTINGS: FontSettings = {
  fontSize: 120,
  lineHeight: 2,
  wordSpacing: 0.25,
  textAlign: 'right',
  baseline: 'middle',
}

// Slider configurations
export const SLIDER_CONFIGS: Record<string, SliderConfig> = {
  density: {
    min: 1,
    max: 25,
    step: 1,
    // Lower default: this value is both the particle size AND the sampling
    // spacing, so a large value under-samples detailed calligraphy into sparse,
    // jumbled chunks. 4 keeps letterforms readable out of the box.
    defaultValue: 4,
    label: 'Size',
    unit: 'px',
  },
  fontSize: {
    min: 24,
    max: 300,
    step: 4,
    defaultValue: 120,
    label: 'Font Size',
    unit: 'px',
  },
  lineHeight: {
    min: 1,
    max: 4,
    step: 0.1,
    defaultValue: 2,
    label: 'Line Height',
  },
  wordSpacing: {
    min: 0,
    max: 2,
    step: 0.05,
    defaultValue: 0.25,
    label: 'Word Spacing',
  },
  orderAmount: {
    min: 0,
    max: 1,
    step: 0.001,
    defaultValue: 1,
    label: 'Order',
  },
  particleScale: {
    min: 0.2,
    max: 3,
    step: 0.05,
    defaultValue: 1,
    label: 'Particle Scale',
  },
  animationSpeed: {
    min: 0.1,
    max: 100,
    step: 0.1,
    defaultValue: 1,
    label: 'Speed',
    unit: 'x',
  },
  holdAtOrder: {
    min: 0,
    max: 30,
    step: 0.5,
    defaultValue: 0,
    label: 'Hold Order',
    unit: 's',
  },
  holdAtChaos: {
    min: 0,
    max: 30,
    step: 0.5,
    defaultValue: 0,
    label: 'Hold Chaos',
    unit: 's',
  },
  wiggleDampening: {
    min: 0.1,
    max: 1.0,
    step: 0.01,
    defaultValue: 0.95,
    label: 'Dampening',
  },
  borderRadius: {
    min: 0,
    max: 0.5,
    step: 0.01,
    defaultValue: 0.08,
    label: 'Radius',
  },
  glowSoftness: {
    min: 0,
    max: 60,
    step: 1,
    defaultValue: 24,
    label: 'Softness',
    unit: 'px',
  },
}

// Animation settings
// Speed is now in units per second (not per frame)
// Base speed: 1.0 means a full chaos->order sweep takes ~1 second at 1x speed
// (the Speed control scales this from 0.1x up to 100x).
export const ANIMATION_SPEED_PER_SECOND = 1.0

/**
 * Particle Physics Configuration
 * Values are per-frame for simplicity (particle physics runs at display refresh rate)
 * Frame-rate independence is handled at the animation level, not particle physics level
 */
export const PARTICLE_PHYSICS = {
  // === Velocity ===
  // Maximum speed in pixels per frame
  maxSpeed: 4,

  // === Acceleration ===
  // Maximum steering force per frame
  maxForce: 0.15,

  // === Arrival Behavior ===
  // Multiplier for arrive radius based on particle size
  // arriveRadius = particleSize * arriveRadiusMultiplier
  arriveRadiusMultiplier: 15,

  // === Separation/Collision ===
  // True radius multiplier (0.5 = actual edge, use slightly higher for visual buffer)
  separationRadiusMultiplier: 0.5,

  // Buffer zone around particles (1.2 = 20% extra space)
  collisionBuffer: 1.2,

  // Minimum distance to prevent division by zero
  minDistanceEpsilon: 0.001,

  // Separation force factor (1.0 = default strength)
  separationForceFactor: 1.0,
}

// Accepted font file types
export const ACCEPTED_FONT_TYPES = [
  '.ttf',
  '.otf',
  '.woff',
  '.woff2',
]

export const ACCEPTED_FONT_MIME_TYPES = [
  'font/ttf',
  'font/otf',
  'font/woff',
  'font/woff2',
  'application/x-font-ttf',
  'application/x-font-otf',
]

// IndexedDB config
export const FONT_STORAGE_KEY = 'calligraphy-fonts'

// Background gradient colors
export const BACKGROUND_COLORS = {
  top: { r: 26, g: 26, b: 46 },
  bottom: { r: 15, g: 52, b: 96 },
}
